import React, { useState, useEffect } from 'react';
import { usmleAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { HighlightableText } from '../components/HighlightableText';
import { cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import {
    Loader2,
    CheckCircle,
    XCircle,
    ArrowRight,
    Clock,
    Lightbulb,
    BookOpen,
    GraduationCap,
    Stethoscope,
    Brain,
    Target,
    Trophy,
    Play
} from 'lucide-react';
import Layout from '../components/Layout';

const STEP_ICONS = {
    step1: Brain,
    step2: Stethoscope,
    step3: Target
};

const STEP_COLORS = {
    step1: { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-200' },
    step2: { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50', border: 'border-emerald-200' },
    step3: { bg: 'bg-purple-500', text: 'text-purple-600', light: 'bg-purple-50', border: 'border-purple-200' }
};

const DIFFICULTY_COLORS = {
    easy: { bg: "bg-emerald-100", text: "text-emerald-700" },
    medium: { bg: "bg-amber-100", text: "text-amber-700" },
    hard: { bg: "bg-red-100", text: "text-red-700" },
};

export default function USMLESteps() {
    const { isDark } = useTheme();
    const [steps, setSteps] = useState([]);
    const [selectedStep, setSelectedStep] = useState('step1');
    const [questionCount, setQuestionCount] = useState('10');
    const [loading, setLoading] = useState(false);
    const [loadingSteps, setLoadingSteps] = useState(true);
    
    // Session state
    const [session, setSession] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState(null);
    const [strikethrough, setStrikethrough] = useState([]);
    const [startTime, setStartTime] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [sessionStats, setSessionStats] = useState({ correct: 0, total: 0 });

    // Load steps info
    useEffect(() => {
        const loadSteps = async () => {
            try {
                const response = await usmleAPI.getSteps();
                setSteps(response.data.steps);
            } catch (err) {
                console.error('Failed to load steps:', err);
                toast.error('Failed to load USMLE step information');
            } finally {
                setLoadingSteps(false);
            }
        };
        loadSteps();
    }, []);

    // Timer effect
    useEffect(() => {
        let interval;
        if (startTime && !submitted && currentQuestion) {
            interval = setInterval(() => {
                setElapsed(Math.floor((Date.now() - startTime) / 1000));
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [startTime, submitted, currentQuestion]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const startSession = async () => {
        setLoading(true);
        try {
            const response = await usmleAPI.startSession(selectedStep, parseInt(questionCount));
            setSession(response.data);
            setSessionStats({ correct: 0, total: 0 });
            await loadNextQuestion(response.data.session_id);
        } catch (err) {
            console.error('Failed to start session:', err);
            toast.error(err.response?.data?.detail || 'Failed to start practice session');
        } finally {
            setLoading(false);
        }
    };

    const loadNextQuestion = async (sessionId) => {
        setLoading(true);
        setSelectedAnswer(null);
        setSubmitted(false);
        setResult(null);
        setStrikethrough([]);
        setStartTime(null);
        setElapsed(0);

        try {
            const response = await usmleAPI.getQuestion(sessionId);
            
            if (response.data.status === 'completed') {
                // Session completed
                setCurrentQuestion(null);
                toast.success('Session completed! Great work!');
            } else {
                setCurrentQuestion(response.data.question);
                setProgress(response.data.progress);
                setStartTime(Date.now());
            }
        } catch (err) {
            console.error('Failed to load question:', err);
            toast.error('Failed to load next question');
        } finally {
            setLoading(false);
        }
    };

    const submitAnswer = async () => {
        if (!selectedAnswer || !currentQuestion || !session) return;

        try {
            const response = await usmleAPI.submitAnswer(session.session_id, currentQuestion.id, selectedAnswer);
            setResult(response.data);
            setSubmitted(true);
            
            // Update stats
            setSessionStats(prev => ({
                correct: prev.correct + (response.data.is_correct ? 1 : 0),
                total: prev.total + 1
            }));
            
            if (response.data.is_correct) {
                toast.success('Correct! Well done!');
            } else {
                toast.error('Incorrect. Review the explanation below.');
            }
        } catch (err) {
            console.error('Failed to submit answer:', err);
            const detail = err.response?.data?.detail || err.message || 'Unknown error';
            toast.error(`Failed to submit answer: ${detail}`);
        }
    };

    const toggleStrikethrough = (letter) => {
        if (submitted) return;
        setStrikethrough(prev => 
            prev.includes(letter) 
                ? prev.filter(l => l !== letter)
                : [...prev, letter]
        );
    };

    const endSession = () => {
        setSession(null);
        setCurrentQuestion(null);
        setProgress({ current: 0, total: 0 });
        setSessionStats({ correct: 0, total: 0 });
    };

    const currentStepInfo = steps.find(s => s.id === selectedStep);
    const stepColors = STEP_COLORS[selectedStep];
    const StepIcon = STEP_ICONS[selectedStep] || GraduationCap;

    if (loadingSteps) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
            </Layout>
        );
    }

    // Session completed view
    if (session && !currentQuestion && !loading) {
        const accuracy = sessionStats.total > 0 
            ? Math.round((sessionStats.correct / sessionStats.total) * 100) 
            : 0;

        return (
            <Layout>
                <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="session-complete">
                    <Card className={cn(
                        "border-2",
                        isDark ? "bg-slate-800 border-slate-700" : "border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50"
                    )}>
                        <CardContent className="p-8 text-center">
                            <div className={cn(
                                "w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center",
                                isDark ? "bg-emerald-900/50" : "bg-emerald-100"
                            )}>
                                <Trophy className={cn("h-10 w-10", isDark ? "text-emerald-400" : "text-emerald-600")} />
                            </div>
                            <h2 className={cn(
                                "text-2xl font-bold mb-2",
                                isDark ? "text-white" : "text-slate-900"
                            )} style={{ fontFamily: 'Manrope' }}>
                                Session Complete!
                            </h2>
                            <p className={cn("mb-6", isDark ? "text-slate-300" : "text-slate-600")}>
                                You've completed your {session.step_name} practice session
                            </p>
                            
                            <div className={cn(
                                "rounded-lg p-6 mb-6",
                                isDark ? "bg-slate-900/50" : "bg-white"
                            )}>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-slate-900")}>
                                            {sessionStats.correct}
                                        </p>
                                        <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Correct</p>
                                    </div>
                                    <div>
                                        <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-slate-900")}>
                                            {sessionStats.total}
                                        </p>
                                        <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Total</p>
                                    </div>
                                    <div>
                                        <p className={cn(
                                            "text-3xl font-bold",
                                            accuracy >= 70 ? "text-emerald-600" : accuracy >= 50 ? "text-amber-600" : "text-red-600"
                                        )}>
                                            {accuracy}%
                                        </p>
                                        <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Accuracy</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 justify-center">
                                <Button variant="outline" onClick={endSession} data-testid="back-to-steps-btn">
                                    Back to Steps
                                </Button>
                                <Button onClick={startSession} data-testid="start-new-session-btn">
                                    <Play className="mr-2 h-4 w-4" />
                                    Start New Session
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        );
    }

    // Active session view
    if (session && currentQuestion) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="usmle-session">
                    {/* Session Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <Badge className={cn(stepColors.light, stepColors.text, stepColors.border)}>
                                <StepIcon className="h-4 w-4 mr-1" />
                                {session.step_name}
                            </Badge>
                            <Badge variant="outline" className={isDark ? "border-slate-600" : ""}>
                                Question {progress.current} of {progress.total}
                            </Badge>
                            <Badge className={cn(
                                DIFFICULTY_COLORS[currentQuestion.difficulty]?.bg,
                                DIFFICULTY_COLORS[currentQuestion.difficulty]?.text
                            )}>
                                {currentQuestion.difficulty?.charAt(0).toUpperCase() + currentQuestion.difficulty?.slice(1)}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className={cn(
                                "flex items-center gap-2",
                                isDark ? "text-slate-300" : "text-slate-500"
                            )}>
                                <Clock className="h-4 w-4" />
                                <span className="font-mono" data-testid="timer">{formatTime(elapsed)}</span>
                            </div>
                            <div className={cn(
                                "text-sm",
                                isDark ? "text-slate-400" : "text-slate-500"
                            )}>
                                Score: {sessionStats.correct}/{sessionStats.total}
                            </div>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={endSession}
                                className={cn("text-red-500 hover:text-red-600", isDark && "hover:bg-slate-800")}
                                data-testid="end-session-btn"
                            >
                                End Session
                            </Button>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className={cn("h-2 rounded-full overflow-hidden", isDark ? "bg-slate-700" : "bg-slate-200")}>
                        <div 
                            className={cn("h-full transition-all duration-300", stepColors.bg)}
                            style={{ width: `${(progress.current / progress.total) * 100}%` }}
                        />
                    </div>

                    {/* Vignette */}
                    <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "border-slate-200 bg-slate-50")}>
                        <CardContent className="p-6">
                            <div className="vignette-text" data-testid="question-vignette">
                                <HighlightableText 
                                    text={currentQuestion.vignette} 
                                    className={cn("leading-relaxed", isDark ? "text-slate-200" : "text-slate-700")}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Question Stem & Options */}
                    <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "border-slate-200")}>
                        <CardContent className="p-6">
                            <p className={cn(
                                "text-lg font-medium mb-6",
                                isDark ? "text-white" : "text-slate-900"
                            )} data-testid="question-stem">
                                {currentQuestion.question_stem}
                            </p>

                            {/* Options */}
                            <div className="space-y-3">
                                {currentQuestion.options?.map((option) => {
                                    const isSelected = selectedAnswer === option.letter;
                                    const isCorrect = submitted && option.letter === result?.correct_answer;
                                    const isWrong = submitted && isSelected && !isCorrect;
                                    const isStruckThrough = strikethrough.includes(option.letter);

                                    return (
                                        <div
                                            key={option.letter}
                                            onClick={() => !submitted && setSelectedAnswer(option.letter)}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                toggleStrikethrough(option.letter);
                                            }}
                                            className={cn(
                                                "flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                                                isDark ? "border-slate-600" : "border-slate-200",
                                                !submitted && !isDark && "hover:border-indigo-300 hover:bg-indigo-50/50",
                                                !submitted && isDark && "hover:border-indigo-500 hover:bg-indigo-900/20",
                                                isSelected && !submitted && !isDark && "border-indigo-500 bg-indigo-50",
                                                isSelected && !submitted && isDark && "border-indigo-500 bg-indigo-900/30",
                                                isCorrect && !isDark && "border-emerald-500 bg-emerald-100",
                                                isCorrect && isDark && "border-emerald-500 bg-emerald-900/60",
                                                isWrong && !isDark && "border-red-500 bg-red-100",
                                                isWrong && isDark && "border-red-500 bg-red-900/60",
                                                isStruckThrough && !submitted && "opacity-50"
                                            )}
                                            data-testid={`option-${option.letter}`}
                                        >
                                            <div className={cn(
                                                "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm",
                                                !submitted && isSelected && "bg-indigo-600 text-white",
                                                !submitted && !isSelected && (isDark ? "bg-slate-700 text-slate-300" : "bg-slate-200 text-slate-600"),
                                                isCorrect && "bg-emerald-600 text-white",
                                                isWrong && "bg-red-600 text-white"
                                            )}>
                                                {submitted && isCorrect && <CheckCircle className="h-5 w-5" />}
                                                {submitted && isWrong && <XCircle className="h-5 w-5" />}
                                                {!submitted && option.letter}
                                                {submitted && !isCorrect && !isWrong && option.letter}
                                            </div>
                                            <span className={cn(
                                                "flex-1",
                                                isStruckThrough && !submitted && "line-through text-slate-400",
                                                isCorrect && !isDark && "text-emerald-900 font-medium",
                                                isCorrect && isDark && "text-emerald-200 font-medium",
                                                isWrong && !isDark && "text-red-900",
                                                isWrong && isDark && "text-red-200",
                                                !submitted && !isStruckThrough && (isDark ? "text-slate-200" : "text-slate-700")
                                            )}>
                                                {option.text}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Tip */}
                            {!submitted && (
                                <p className={cn(
                                    "mt-4 text-sm flex items-center gap-2",
                                    isDark ? "text-slate-400" : "text-slate-500"
                                )}>
                                    <Lightbulb className="h-4 w-4" />
                                    Tip: Right-click an option to strike through it
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3">
                        {!submitted ? (
                            <Button 
                                onClick={submitAnswer}
                                disabled={!selectedAnswer}
                                size="lg"
                                data-testid="submit-answer-btn"
                            >
                                Submit Answer
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        ) : (
                            <Button 
                                onClick={() => loadNextQuestion(session.session_id)}
                                size="lg"
                                data-testid="next-question-btn"
                            >
                                Next Question
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        )}
                    </div>

                    {/* Explanation */}
                    {submitted && result && (
                        <Card className={cn(
                            "border-2",
                            result.is_correct && !isDark && "border-emerald-500 bg-emerald-100",
                            result.is_correct && isDark && "border-emerald-500 bg-emerald-900/50",
                            !result.is_correct && !isDark && "border-amber-500 bg-amber-100",
                            !result.is_correct && isDark && "border-amber-500 bg-amber-900/50"
                        )}>
                            <CardHeader className="pb-2">
                                <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
                                    {result.is_correct ? (
                                        <>
                                            <CheckCircle className="h-5 w-5 text-emerald-500" />
                                            <span className={isDark ? "text-emerald-300" : "text-emerald-800"}>Correct!</span>
                                        </>
                                    ) : (
                                        <>
                                            <BookOpen className="h-5 w-5 text-amber-500" />
                                            <span className={isDark ? "text-amber-300" : "text-amber-800"}>Explanation</span>
                                        </>
                                    )}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className={cn(
                                    "leading-relaxed",
                                    result.is_correct && !isDark && "text-emerald-900",
                                    result.is_correct && isDark && "text-emerald-200",
                                    !result.is_correct && !isDark && "text-amber-900",
                                    !result.is_correct && isDark && "text-amber-200"
                                )} data-testid="explanation-text">
                                    {result.explanation}
                                </p>
                                {result.concept && (
                                    <p className={cn(
                                        "mt-3 text-sm font-medium",
                                        result.is_correct && !isDark && "text-emerald-800",
                                        result.is_correct && isDark && "text-emerald-300",
                                        !result.is_correct && !isDark && "text-amber-800",
                                        !result.is_correct && isDark && "text-amber-300"
                                    )}>
                                        Key Concept: {result.concept}
                                    </p>
                                )}
                                {!result.is_correct && (
                                    <p className={cn("mt-3 text-sm", isDark ? "text-amber-300" : "text-amber-800")}>
                                        The correct answer was <strong>{result.correct_answer}</strong>.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>
            </Layout>
        );
    }

    // Step selection view
    return (
        <Layout>
            <div className="max-w-5xl mx-auto space-y-6 animate-fade-in" data-testid="usmle-steps-page">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className={cn(
                        "text-3xl md:text-4xl font-bold mb-3",
                        isDark ? "text-white" : "text-slate-900"
                    )} style={{ fontFamily: 'Manrope' }}>
                        USMLE Step Practice
                    </h1>
                    <p className={cn("max-w-2xl mx-auto", isDark ? "text-slate-300" : "text-slate-600")}>
                        Practice board-style questions tailored to each USMLE Step exam. 
                        Select your step and start a focused study session.
                    </p>
                </div>

                {/* Step Tabs */}
                <Tabs value={selectedStep} onValueChange={setSelectedStep} className="w-full">
                    <TabsList className={cn(
                        "w-full grid grid-cols-3 h-auto p-1",
                        isDark ? "bg-slate-800" : "bg-slate-100"
                    )}>
                        {steps.map((step) => {
                            const Icon = STEP_ICONS[step.id] || GraduationCap;
                            const colors = STEP_COLORS[step.id];
                            return (
                                <TabsTrigger 
                                    key={step.id} 
                                    value={step.id}
                                    className={cn(
                                        "flex flex-col sm:flex-row items-center gap-2 py-3 px-4 data-[state=active]:shadow-md",
                                        `data-[state=active]:${colors.light} data-[state=active]:${colors.text}`
                                    )}
                                    data-testid={`tab-${step.id}`}
                                >
                                    <Icon className="h-5 w-5" />
                                    <span className="font-medium">{step.name}</span>
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>

                    {steps.map((step) => {
                        const Icon = STEP_ICONS[step.id] || GraduationCap;
                        const colors = STEP_COLORS[step.id];
                        return (
                            <TabsContent key={step.id} value={step.id} className="mt-6">
                                <Card className={cn(
                                    "border-2",
                                    isDark ? "bg-slate-800 border-slate-700" : colors.border
                                )}>
                                    <CardHeader>
                                        <div className="flex items-start gap-4">
                                            <div className={cn(
                                                "p-3 rounded-xl",
                                                isDark ? "bg-slate-700" : colors.light
                                            )}>
                                                <Icon className={cn("h-8 w-8", colors.text)} />
                                            </div>
                                            <div className="flex-1">
                                                <CardTitle className={cn(
                                                    "text-2xl mb-2",
                                                    isDark ? "text-white" : "text-slate-900"
                                                )} style={{ fontFamily: 'Manrope' }}>
                                                    {step.full_name}
                                                </CardTitle>
                                                <CardDescription className={cn(
                                                    "text-base",
                                                    isDark ? "text-slate-300" : "text-slate-600"
                                                )}>
                                                    {step.description}
                                                </CardDescription>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {/* Subjects */}
                                        <div>
                                            <h4 className={cn(
                                                "text-sm font-medium mb-3",
                                                isDark ? "text-slate-300" : "text-slate-700"
                                            )}>
                                                Covered Subjects
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {step.subjects?.map((subject) => (
                                                    <Badge 
                                                        key={subject} 
                                                        variant="secondary"
                                                        className={cn(
                                                            isDark ? "bg-slate-700 text-slate-200" : ""
                                                        )}
                                                    >
                                                        {subject}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Session Setup */}
                                        <div className={cn(
                                            "rounded-lg p-4",
                                            isDark ? "bg-slate-900/50" : "bg-slate-50"
                                        )}>
                                            <h4 className={cn(
                                                "text-sm font-medium mb-3",
                                                isDark ? "text-slate-300" : "text-slate-700"
                                            )}>
                                                Start Practice Session
                                            </h4>
                                            <div className="flex flex-col sm:flex-row gap-4 items-end">
                                                <div className="flex-1 space-y-2">
                                                    <label className={cn(
                                                        "text-sm",
                                                        isDark ? "text-slate-400" : "text-slate-600"
                                                    )}>
                                                        Number of Questions
                                                    </label>
                                                    <Select value={questionCount} onValueChange={setQuestionCount}>
                                                        <SelectTrigger 
                                                            className={cn(isDark && "bg-slate-800 border-slate-600")}
                                                            data-testid="question-count-select"
                                                        >
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {[5, 10, 15, 20, 25, 30, 40, 50].map(n => (
                                                                <SelectItem key={n} value={n.toString()}>
                                                                    {n} questions
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <Button 
                                                    onClick={startSession}
                                                    disabled={loading}
                                                    size="lg"
                                                    className={cn(
                                                        "w-full sm:w-auto",
                                                        colors.bg,
                                                        "hover:opacity-90 text-white"
                                                    )}
                                                    data-testid="start-session-btn"
                                                >
                                                    {loading ? (
                                                        <>
                                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                            Starting...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Play className="mr-2 h-4 w-4" />
                                                            Start {step.name} Practice
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        );
                    })}
                </Tabs>
            </div>
        </Layout>
    );
}
